import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs';
import { ModelOperation, OtaService } from 'src/app/Core/services/ota.service';
import { ToasterService } from 'src/app/Core/services/toaster.service';

interface OperationPage {
  pageNumber: number;
  cursor: string | null;
  records: ModelOperation[];
  nextCursor: string | null;
}

@Component({
  selector: 'app-ota-operations',
  templateUrl: './ota-operations.component.html',
  styleUrls: ['./ota-operations.component.css']
})
export class OtaOperationsComponent implements OnInit {
  readonly pageSize = 5;
  readonly errorPreviewLength = 18;
  currentPage = 1;
  isLoading = false;
  operationPages: OperationPage[] = [];

  constructor(private otaService: OtaService, private toaster: ToasterService,) {}
  
  ngOnInit(): void {
    this.fetchOperations();
  }

  fetchOperations(cursor: string | null = null): void {
    if (this.isLoading) {
      return;
    }

    const pageNumber = this.operationPages.length === 0 ? 1 : this.currentPage + 1;
    this.isLoading = true;

    this.otaService.getModelOperations(this.pageSize, cursor ?? undefined).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: (response) => {
        this.operationPages.push({
          pageNumber,
          cursor,
          records: response.logs ?? [],
          nextCursor: this.normalizeCursor(response.next_cursor)
        });
        this.currentPage = pageNumber;
      },
      error: (error: any) => {
        console.error('Error fetching OTA operations:', error);
        if (error instanceof HttpErrorResponse) {
          this.toaster.openDialogBox(error.error?.message ?? 'Unable to load OTA operations.', false);
        }
      }
    });
  }

  nextPage(): void {
    if (this.isLoading || !this.currentOperationPage?.nextCursor) {
      return;
    }

    const cachedPage = this.getPage(this.currentPage + 1);
    if (cachedPage) {
      this.currentPage = cachedPage.pageNumber;
      return;
    }

    this.fetchOperations(this.currentOperationPage.nextCursor);
  }

  previousPage(): void {
    if (!this.isLoading && this.currentPage > 1) {
      this.currentPage--;
    }
  }

  trackByAuditId(index: number, operation: ModelOperation): string {
    return operation.audit_id || `${operation.timestamp}-${index}`;
  }

  displayValue(value: string | number | null | undefined): string | number {
    return value === null || value === undefined || value === '' ? '-' : value;
  }

  hasError(message: string | null | undefined): boolean {
    return !!message && message.trim().length > 0;
  }

  truncatedError(message: string | null | undefined): string {
    if (!this.hasError(message)) {
      return '-';
    }

    const trimmed = (message as string).trim();
    return trimmed.length > this.errorPreviewLength
      ? `${trimmed.substring(0, this.errorPreviewLength)}...`
      : trimmed;
  }

  viewError(message: string | null | undefined): void {
    if (this.hasError(message)) {
      this.toaster.openDialogBox(message as string, false);
    }
  }

  get currentOperations(): ModelOperation[] {
    return this.currentOperationPage?.records ?? [];
  }

  get currentOperationPage(): OperationPage | undefined {
    return this.getPage(this.currentPage);
  }

  get canGoNext(): boolean {
    return !this.isLoading && !!this.currentOperationPage?.nextCursor;
  }

  private getPage(pageNumber: number): OperationPage | undefined {
    return this.operationPages.find((page) => page.pageNumber === pageNumber);
  }

  private normalizeCursor(cursor: string | null | undefined): string | null {
    return cursor?.trim() || null;
  }

  getEventTypeFor(eventType: string | null | undefined): string {
    if (!eventType) {
      return '-';
    }

    switch (eventType) {
      case 'KEY_GENERATED':
        return 'Key Creation';
      case 'SIGNING_KEY_USED':
        return 'Key Signed';
      case 'SIGNED_METADATA_CREATED':
        return 'OTA Metadata Creation';
      case 'OTA_PRESIGN_UPLOAD':
        return 'File Upload';
      case 'OTA_JOB_CREATED':
        return 'Job Created';
      case 'OTA_JOB_CANCELLED':
        return 'Job Cancelled';
      case 'OTA_JOB_DELETED':
        return 'Job Deleted';
      default:
        return eventType;
    }
  }
}
